/**
 * Default Theme Configuration
 * These values are used as fallbacks when API theme_settings are not available
 */


const defaultTheme = {
    colors: {
        // Primary colors
        primary: '#007EE6',
        primaryHover: '#0066CC',
        primaryLight: '#E6F3FF',
        
        // Secondary colors
        secondary: '#F59E0B',
        secondaryHover: '#D97706',
        secondaryLight: '#FEF3C7',
        
        // Background colors
        background: '#FFFFFF',
        backgroundAlt: '#F9FAFB',
        backgroundDark: '#1F2937',
        
        // Text colors
        text: '#111827',
        textMuted: '#6B7280',
        textLight: '#9CA3AF',
        textInverse: '#FFFFFF',
        
        // Status colors
        success: '#10B981',
        successLight: '#D1FAE5',
        error: '#EF4444',
        errorLight: '#FEE2E2',
        warning: '#F59E0B',
        warningLight: '#FEF3C7',
        info: '#3B82F6',
        infoLight: '#DBEAFE',
        
        // UI colors
        border: '#E5E7EB',
        borderDark: '#D1D5DB',
        divider: '#F3F4F6',
        
        // Component specific
        cardBackground: '#FFFFFF',
        cardBorder: '#E5E7EB',
        headerBackground: '#FFFFFF',
        headerText: '#111827',
        footerBackground: '#1F2937',
        footerText: '#FFFFFF',
        
        // Interactive elements
        buttonPrimaryBg: '#007EE6',
        buttonPrimaryText: '#FFFFFF',
        buttonSecondaryBg: '#F3F4F6',
        buttonSecondaryText: '#374151',
        linkColor: '#007EE6',
        linkHover: '#0066CC',
        
        // Form elements
        inputBackground: '#FFFFFF',
        inputBorder: '#D1D5DB',
        inputFocus: '#007EE6',
        inputPlaceholder: '#9CA3AF',
        
        // Skeleton loading
        skeletonBase: '#F3F3F3',
        skeletonHighlight: '#EBEBEB'
    },

    
    font: {
        family: 'Poppins-Regular, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        familyHeading: 'Poppins-SemiBold, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        familyLight: 'Poppins-Light',
        familyMedium: 'Poppins-Medium',
        familyBold: 'Poppins-Bold',
        
        // Font sizes
        sizeBase: 14,
        sizeXs: 11,
        sizeSm: 12,
        sizeMd: 14,
        sizeLg: 16,
        sizeXl: 18,
        
        // Heading sizes
        sizeH1: 36,
        sizeH2: 30,
        sizeH3: 24,
        sizeH4: 20,
        sizeH5: 18,
        sizeH6: 16,
        
        // Font weights
        weightLight: 300,
        weightNormal: 400,
        weightMedium: 500,
        weightSemiBold: 600,
        weightBold: 700,
        
        // Line heights
        lineHeightTight: 1.25,
        lineHeightNormal: 1.5,
        lineHeightRelaxed: 1.75
    },
    
    spacing: {
        borderRadius: 8,
        borderRadiusXs: 2,
        borderRadiusSm: 4,
        borderRadiusMd: 8,
        borderRadiusLg: 12,
        borderRadiusXl: 16,
        borderRadiusFull: 9999,
        
        cardPadding: 16,
        sectionGap: 32,
        containerPadding: 16
    },
    
    layout: {
        headerFixed: true,
        headerHeight: 85,
        maxContentWidth: 1200,
        sidebarWidth: 280,
        footerHeight: 'auto'
    },
    
    effects: {
        boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.1)',
        boxShadowSm: '0px 2px 8px rgba(0, 0, 0, 0.08)',
        boxShadowLg: '0px 8px 24px rgba(0, 0, 0, 0.15)',
        boxShadowCard: '0px 4px 20px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.3s ease',
        transitionFast: 'all 0.15s ease',
        transitionSlow: 'all 0.5s ease'
    }
};

/**
 * Merge API theme settings with default theme
 * @param {Object} apiTheme - Theme settings from API
 * @returns {Object} - Merged theme object
 */
export const mergeTheme = (apiTheme) => {
    if (!apiTheme) return defaultTheme;
    
    return {
        colors: { ...defaultTheme.colors, ...apiTheme.colors },
        font: { ...defaultTheme.font, ...apiTheme.font },
        spacing: { ...defaultTheme.spacing, ...apiTheme.spacing },
        layout: { ...defaultTheme.layout, ...apiTheme.layout },
        effects: { ...defaultTheme.effects, ...apiTheme.effects }
    };
};

/**
 * Parse theme_settings value from API
 * @param {string|Object} value - Raw theme_settings value from API
 * @returns {Object} - Parsed theme object
 */
export const parseThemeSettings = (value) => {
    if (!value) return null;
    
    try {
        if (typeof value === 'string') {
            return JSON.parse(value);
        }
        return value;
    } catch (error) {
        console.error('Error parsing theme settings:', error);
        return null;
    }
};

export default defaultTheme;
